/**
 * EVERY ENDPOINT IS CONFIGURATION. THERE IS NO HARDCODED FALLBACK, ON PURPOSE.
 * ==========================================================================
 *
 * ⚠️ **THE ISSUER THIS MODULE WAS FIRST WRITTEN AGAINST WAS THE WRONG ONE. THAT IS THE POINT.**
 *
 * This workstream was handed the Cognito user pool `us-east-1_4MmYZszEG` as issuer, with that pool's own
 * `jwks_uri`, as established fact. It is **not** our issuer: it is the IdPrism↔Cognito internal leg. We
 * integrate through **Amazon Federate** (`https://idp.federate.amazon.com`), established three
 * independent ways — see {@link FEDERATE_LEG} — and confirmed by the owner's own HLD.
 *
 * **That correction landed while this file was being written, and it cost nothing, because the values
 * were configuration rather than constants.** Had the Cognito issuer been compiled in, fixing it would
 * now be a code change, a review, a build and a deploy — and until that shipped the app would have been
 * exchanging codes for tokens signed by an issuer the API authorizer will never trust. This is not a
 * hypothetical argument for configuration over constants; it is the one that already happened, twice, in
 * one change. So they are ALL configuration: issuer, JWKS URI, authorize endpoint, token endpoint,
 * client id — and each is overridable per environment, because **whether dev lands on Federate prod or
 * Federate integ is still open**.
 *
 * {@link FEDERATE_LEG} and {@link WRONG_LEG_COGNITO_INTERNAL} record both legs **as documentation only**.
 * Neither is read by {@link readOidcConfig}; there is no code path from either to a config object, and
 * `test/auth/config-and-controls.test.ts` asserts it.
 *
 * **A MISSING VALUE IS A NAMED FAILURE, NOT A GUESS.** `readOidcConfig` reports *every* missing or
 * malformed variable in one go, with the variable names in the message, and `install.ts` leaves the
 * stub token provider in place — so the app keeps sending nothing rather than firing a request that
 * would come back 401 and read like an expired session. A placeholder default would instead redirect a
 * technician away from the app to a Cognito error page reading *"An error was encountered with the
 * requested page"*, which names none of the six things it could be.
 *
 * **THERE IS NO CLIENT SECRET AND THERE MUST NEVER BE ONE.** See {@link NO_CLIENT_SECRET}.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT THE HLD FIXES, AND WHAT IT MEANS FOR THIS APP
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 *  · **Access token 15 minutes. Refresh token 10 hours.** Not values we choose. Consequences are in
 *    `oidc.ts` (refresh-before-expiry is the normal path, not an edge case) and in `phase.ts` (an
 *    11-hour shift outruns the refresh window, and that is an ordinary outcome to word carefully, not
 *    an error).
 *  · **Users self-register on the IdPrism portal** — username, password, and a device-bound FIDO2
 *    passkey protected by biometrics or a PIN. **This app never touches a credential.** It has no
 *    password field, no passkey handling, no username input, and nothing to store: it redirects to the
 *    IdP and receives a code back. Anyone adding a credential input to this app has misread the
 *    architecture.
 *  · **The HLD says of PRISM: *"There is no offline fallback by design — user authentication is a
 *    security requirement."*** That is correct **for PRISM** and must not be copied here. PRISM needs a
 *    live token to operate a locker over Bluetooth; auth in *this* app gates *submission* to an API, and
 *    a submission is inherently deferrable — the outbox exists for exactly that. What the HLD's stance
 *    does impose is that **first sign-in cannot happen in a dead zone**, which is a fact to state
 *    plainly to the technician (and `phase.ts` does) rather than a reason to gate the UI on a token.
 */

/**
 * **THE CORRECT LEG: FEDERATE.** Documentation only — never a default; see the file header.
 *
 * Established three independent ways, none of them by this module: `discovery/auth/idprism.md` §3 (*"your
 * application integrates with Federate. Federate integrates with IdPrism"*), `PRISMApp`'s checked-in
 * client config, and a live fetch of the discovery document recorded in
 * `infra/lib/config/federate.ts`. The owner's Fresnel/PRISM HLD agrees: Fresnel's authorizer validates
 * *"using Amazon Federate's JWKS endpoint"*.
 *
 * ⚠️ **NO `authorizationEndpoint` OR `tokenEndpoint` IS RECORDED HERE, AND THAT IS DELIBERATE.** Neither
 * was observed. They must be read from Federate's own discovery document
 * (`https://idp.federate.amazon.com/.well-known/openid-configuration`) and supplied as configuration —
 * **and it is still open whether dev lands on Federate prod or Federate integ**, which are different
 * hosts. Writing a plausible `/api/oauth2/v2/authorize` here would be precisely the
 * "plausible-but-wrong" value that `infra/lib/config/federate.ts` refuses to default: an endpoint that
 * is right for the wrong environment fails at the IdP, after the technician has been redirected away.
 */
export const FEDERATE_LEG = {
  issuer: 'https://idp.federate.amazon.com',
  discoveryDocument: 'https://idp.federate.amazon.com/.well-known/openid-configuration',
  jwksUri: 'https://idp.federate.amazon.com/api/oauth2/v2/certs',
  /** The IdPrism userpool as Federate names it. Selected on the service profile, not sent by us. */
  authProvider: 'IDPRISM_LOCKERTECHNICIANS_PROD_NA',
  idTokenSigningAlg: 'RS256',
} as const;

/**
 * ⚠️⚠️ **THE WRONG LEG. DO NOT CONFIGURE ANY OF THESE. RECORDED SO IT IS NOT REDISCOVERED AS A GOOD IDEA.**
 *
 * These are the Cognito user pool's own discovery values and its hosted-UI domain. They were handed to
 * this workstream as established fact, and this module was first written against them. **They are the
 * IdPrism↔Cognito INTERNAL leg**: the pool is how IdPrism stores identities, not how our app integrates.
 *
 * They are kept, prominently and under this name, for one reason: **they are genuinely reachable, which
 * is exactly what made them convincing.** `us-east-1_4MmYZszEG`'s discovery document returns HTTP 200
 * and its JWKS serves real keys, so nothing about trying them looks like a mistake — the app would
 * redirect, the hosted UI would appear, and the tokens obtained would be signed by an issuer the API
 * authorizer will never trust. Deleting this constant would leave the next person to find the same
 * reachable endpoints and draw the same wrong conclusion.
 *
 * `test/auth/config-and-controls.test.ts` asserts that no code path can turn these into a config.
 */
export const WRONG_LEG_COGNITO_INTERNAL = {
  issuer: 'https://cognito-idp.us-east-1.amazonaws.com/us-east-1_4MmYZszEG',
  jwksUri: 'https://cognito-idp.us-east-1.amazonaws.com/us-east-1_4MmYZszEG/.well-known/jwks.json',
  authorizationEndpoint:
    'https://tenant-lockertechnicians-default-na-hkednb.auth.us-east-1.amazoncognito.com/oauth2/authorize',
  tokenEndpoint:
    'https://tenant-lockertechnicians-default-na-hkednb.auth.us-east-1.amazoncognito.com/oauth2/token',
  whyNot:
    'IdPrism↔Cognito internal leg. Our tokens are issued by Federate, so a token from here would be ' +
    'signed by an issuer the API authorizer does not trust.',
} as const;

/**
 * A marker so a reviewer grepping for `client_secret` finds a statement rather than silence.
 *
 * A secret shipped in a browser bundle is not a secret; it is a published credential with a misleading
 * name. PKCE exists precisely so a public client does not need one. If the app client turns out to be
 * configured as *confidential*, the correct response is to have it reconfigured as public — not to add
 * a secret here, not to put one in an env file, and not to "temporarily" hardcode one for a demo.
 *
 * ⚠️ The pool's discovery document does not list `none` under
 * `token_endpoint_auth_methods_supported`, which for a public client it must. A sibling workstream is
 * verifying that the app client is created as a public client. **"Public client" is therefore an
 * assumption in this code, not an established fact.**
 */
export const NO_CLIENT_SECRET = true as const;

/**
 * `S256` only. `plain` is never sent.
 *
 * ⚠️ `code_challenge_methods_supported` is absent from the pool's discovery document. Cognito's
 * document is incomplete on that point — it does support `S256` — but that is read, not verified here,
 * and a sibling workstream is confirming it. A pool that refused `S256` would be a blocker to escalate,
 * never a reason to downgrade to `plain`.
 */
export const CODE_CHALLENGE_METHOD = 'S256' as const;

/**
 * Fixed by the platform per the HLD: **access token 15 minutes, refresh token 10 hours.** Recorded as
 * constants because both drive wording and arithmetic elsewhere, and because a reader needs to see
 * that they are given rather than chosen.
 */
export const PLATFORM_ACCESS_TOKEN_TTL_MS = 15 * 60 * 1000;
export const PLATFORM_REFRESH_TOKEN_TTL_MS = 10 * 60 * 60 * 1000;

export interface OidcConfig {
  readonly issuer: string;
  readonly authorizationEndpoint: string;
  readonly tokenEndpoint: string;
  readonly jwksUri: string;
  /** Optional: sign-out clears local state with or without it. See `OidcAuth.signOut`. */
  readonly endSessionEndpoint: string | null;
  /** Optional and currently never called. See `OidcAuth.signOut` for why. */
  readonly revocationEndpoint: string | null;
  readonly clientId: string;
  /**
   * Must match a redirect URI registered on the app client **byte for byte**, including any trailing
   * slash. Defaults to the app's own base URL — the one URL the app is certainly served from, and
   * sufficient because this app routes on the hash, so no separate `/callback` path has to be
   * registered or served.
   */
  readonly redirectUri: string;
  readonly scopes: readonly string[];
}

export interface MissingConfigValue {
  readonly variable: string;
  readonly problem: 'missing' | 'not-an-absolute-https-url';
  readonly what: string;
}

export type OidcConfigResult =
  | { readonly ok: true; readonly config: OidcConfig }
  | {
      readonly ok: false;
      readonly missing: readonly MissingConfigValue[];
      /** One sentence for a human. It goes on screen and into the console, so it names the variables. */
      readonly message: string;
    };

/**
 * `openid` ALONE by default, and that is deliberately conservative.
 *
 * An app client only receives the scopes its own *allowed OAuth scopes* list permits, and asking for
 * one that is not allowed fails the whole authorization request with `invalid_scope` — at the IdP,
 * after the technician has already been redirected away. The app client does not exist yet, so its
 * allowed list is unknown and `openid` is the only scope that is certain.
 *
 * ⚠️ **`email` is required for C-12 technician attribution.** `POST /submissions` answers
 * `503 C-12-technician-attribution` while the authorizer's context carries no verified `email`, so this
 * app cannot actually submit anything until that claim arrives. Per `discovery/auth/idprism.md` §4 the
 * claim may come from the Federate profile's **Claim Configuration** rather than from an OAuth scope,
 * which is why widening this default is not obviously the fix — and why it is a one-variable change
 * (`VITE_OIDC_SCOPES="openid email"`) once somebody knows which mechanism delivers it.
 */
const DEFAULT_SCOPES = ['openid'] as const;

type EnvLike = Readonly<Record<string, unknown>>;

function readString(env: EnvLike, key: string): string | undefined {
  const raw = env[key];
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

/**
 * `https://…` and parseable. `http://` is refused for anything but `localhost`: an OIDC endpoint over
 * plain HTTP would carry the authorization code and the token in cleartext, and a typo that downgraded
 * the scheme would otherwise be invisible.
 */
function isAbsoluteHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol === 'https:') {
      return true;
    }
    return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1');
  } catch {
    return false;
  }
}

interface RequiredSpec {
  readonly variable: string;
  readonly what: string;
  readonly url: boolean;
}

const REQUIRED: readonly RequiredSpec[] = [
  { variable: 'VITE_OIDC_CLIENT_ID', what: 'the app client id on the identity pool', url: false },
  { variable: 'VITE_OIDC_ISSUER', what: 'the token issuer (`iss`) this app expects', url: true },
  { variable: 'VITE_OIDC_AUTHORIZATION_ENDPOINT', what: 'where to redirect for sign-in', url: true },
  { variable: 'VITE_OIDC_TOKEN_ENDPOINT', what: 'where to exchange the authorization code', url: true },
  { variable: 'VITE_OIDC_JWKS_URI', what: 'the signing keys the API authorizer validates against', url: true },
];

/**
 * Assemble the configuration, or say precisely what is missing — **all of it at once**.
 *
 * Reporting one missing variable at a time turns configuring a build into five deploy cycles, and this
 * app is configured by whoever is on shift rather than by whoever wrote it.
 *
 * `env` and `origin` are parameters rather than direct reads of `import.meta.env` and `location`, so
 * this is exercisable in the node test project where neither exists usefully.
 */
export function readOidcConfig(
  env: EnvLike = (import.meta.env ?? {}) as EnvLike,
  origin: string | undefined = globalThis.location?.origin,
): OidcConfigResult {
  const missing: MissingConfigValue[] = [];
  const values = new Map<string, string>();

  for (const spec of REQUIRED) {
    const value = readString(env, spec.variable);
    if (value === undefined) {
      missing.push({ variable: spec.variable, problem: 'missing', what: spec.what });
      continue;
    }
    if (spec.url && !isAbsoluteHttpsUrl(value)) {
      missing.push({ variable: spec.variable, problem: 'not-an-absolute-https-url', what: spec.what });
      continue;
    }
    values.set(spec.variable, value);
  }

  const explicitRedirect = readString(env, 'VITE_OIDC_REDIRECT_URI');
  const base = readString(env, 'BASE_URL') ?? '/';
  const redirectUri =
    explicitRedirect ??
    (origin === undefined ? undefined : `${origin}${base}`.replace(/([^:])\/{2,}/g, '$1/'));
  if (redirectUri === undefined) {
    missing.push({
      variable: 'VITE_OIDC_REDIRECT_URI',
      problem: 'missing',
      what: 'where the IdP sends the technician back to (no browser origin was available to derive it)',
    });
  }

  if (missing.length > 0 || redirectUri === undefined) {
    return {
      ok: false,
      missing,
      message:
        `Sign-in is not configured in this build: ${missing
          .map((entry) => `${entry.variable} (${entry.problem === 'missing' ? 'not set' : 'not an absolute https URL'}) — ${entry.what}`)
          .join('; ')}. Nothing is sent to the server until every one of those is supplied at build time. ` +
        'The app client must be created on the identity pool as a PUBLIC client with PKCE and no secret.',
    };
  }

  const scopesRaw = readString(env, 'VITE_OIDC_SCOPES');
  const scopes =
    scopesRaw === undefined ? [...DEFAULT_SCOPES] : scopesRaw.split(/\s+/).filter((scope) => scope.length > 0);

  const endSession = readString(env, 'VITE_OIDC_END_SESSION_ENDPOINT');
  const revocation = readString(env, 'VITE_OIDC_REVOCATION_ENDPOINT');

  return {
    ok: true,
    config: {
      // `??` with a throw is unreachable — every key is in `values` or the guard above returned —
      // but `noUncheckedIndexedAccess` requires the narrowing, and an explicit throw is a better
      // record of "this cannot happen" than a `!`.
      clientId: required(values, 'VITE_OIDC_CLIENT_ID'),
      issuer: required(values, 'VITE_OIDC_ISSUER'),
      authorizationEndpoint: required(values, 'VITE_OIDC_AUTHORIZATION_ENDPOINT'),
      tokenEndpoint: required(values, 'VITE_OIDC_TOKEN_ENDPOINT'),
      jwksUri: required(values, 'VITE_OIDC_JWKS_URI'),
      endSessionEndpoint: endSession !== undefined && isAbsoluteHttpsUrl(endSession) ? endSession : null,
      revocationEndpoint: revocation !== undefined && isAbsoluteHttpsUrl(revocation) ? revocation : null,
      redirectUri,
      scopes: scopes.length > 0 ? scopes : [...DEFAULT_SCOPES],
    },
  };
}

function required(values: Map<string, string>, key: string): string {
  const value = values.get(key);
  if (value === undefined) {
    throw new Error(`internal: ${key} passed validation but is absent`);
  }
  return value;
}

/**
 * A ready-to-paste `.env` block, for whoever configures a build.
 *
 * A FUNCTION, not a committed file, and it takes the client id AND THE LEG as arguments — so the
 * observed values stay reachable as documentation while nothing on disk pretends to be a working
 * configuration, no placeholder client id can be committed and shipped, and **nobody gets a complete
 * env block without having decided which OIDC leg they are on.** `.env` and `.env.*` are git-ignored
 * (see the repository `.gitignore`).
 *
 * The `federate` leg deliberately emits the authorize and token endpoints as `TODO` comments rather
 * than guesses: they were never observed. An unset variable fails by name at startup, which is what
 * should happen; a guessed one fails at the IdP after the technician has been redirected away.
 */
export function exampleEnvBlock(clientId: string): string {
  return [
    '# Sign-in configuration. The FEDERATE leg — see config.ts for why the Cognito pool is the wrong one.',
    `# Auth provider on the Federate service profile: ${FEDERATE_LEG.authProvider}`,
    `VITE_OIDC_CLIENT_ID=${clientId}`,
    '# `openid` alone is the only scope certain to be allowed. `email` is needed for C-12 — see config.ts.',
    'VITE_OIDC_SCOPES=openid',
    `VITE_OIDC_ISSUER=${FEDERATE_LEG.issuer}`,
    `VITE_OIDC_JWKS_URI=${FEDERATE_LEG.jwksUri}`,
    '#',
    '# ⚠️ THE TWO BELOW ARE INTENTIONALLY BLANK. They were never observed, and whether this environment',
    '# is Federate PROD or Federate INTEG is still open — different hosts. Read them from the discovery',
    `# document (${FEDERATE_LEG.discoveryDocument}) for the environment you are configuring.`,
    '# An unset value fails at startup, by name. A guessed one fails at the IdP, after the technician has',
    '# already been redirected away from the app.',
    'VITE_OIDC_AUTHORIZATION_ENDPOINT=',
    'VITE_OIDC_TOKEN_ENDPOINT=',
    '#',
    '# Optional. Point VITE_OIDC_TOKEN_ENDPOINT at a pass-through proxy instead if Federate’s token',
    '# endpoint turns out not to answer a browser fetch — see exchange.ts.',
    'VITE_OIDC_END_SESSION_ENDPOINT=',
    'VITE_OIDC_REVOCATION_ENDPOINT=',
  ].join('\n');
}
